import React, { createContext, Dispatch, SetStateAction, useCallback, useState } from 'react'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import StateProvider from '../../../providers/default/StateProvider'
import useT from '../../../i18ns/admin/useT'
import { useRedirectHandleBack, useDialogHandleSubmit } from '../../../utils/default/ComplexFlowHook'
import { Path } from '../../../components/admin/route/route'
import { useCommonStyles, useDialogStyles } from '../../../utils/admin/StyleHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import PlayerSearchForm from '../../../components/admin/manual/PlayerSearchForm'
import ManualWithdrawCreateForm, { formToRequest, FormType } from '../../../components/admin/manual/ManualWithdrawCreateForm'
import ManualWithdrawFileCreateForm from '../../../components/admin/manual/ManualWithdrawFileCreateForm'
import manualWithdrawName from '../../../constants/default/manualWithdrawName'
import { formatMoney } from '../../../utils/default/TableHelper'

export interface ContextType { account: string, file: File, isBatch: boolean }

const defaultContext = { account: '', file: new File([], ''), isBatch: false }

export const RequestContext = createContext<[
  ContextType,
  Dispatch<SetStateAction<ContextType>>
]>([defaultContext, () => {}])

const ManualWithdrawCreatePage: React.FC = () => {
  const classes = useCommonStyles()
  const dialogClasses = useDialogStyles()
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: Path.MANUAL_WITHDRAW
  })
  const { t } = useT()
  const gdk = useGDK()
  const [loading, setLoading] = useState<boolean>(false)
  const globalDialog = useGlobalDialog()
  const { handleSubmit } = useDialogHandleSubmit({
    dialogId: 'createManualWithdraw',
    globalDialog,
    getChangeDialogConfig: useCallback((form: FormType) => {
      return createGlobalDialogConfig({
        showIcon: false,
        notUseTypo: true,
        message: (
          <React.Fragment>
            <Typography className={dialogClasses.text} align="center">{t('dialog.confirmCreateManualWithdraw')}</Typography>
            <Typography className={clsx(classes.purpleWord, dialogClasses.text)} align="center">
              [{form.account}] [{t(manualWithdrawName[form.method])}] [{formatMoney(form.cash)}]
            </Typography>
          </React.Fragment>
        )
      })
    }, [t, classes, dialogClasses]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.createManualWithdrawSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => {
      setLoading(true)
      return gdk.manual.createWithdraw(payload)
    },
    onSuccess: () => {
      setLoading(false)
    },
    onError: () => {
      setLoading(false)
    },
    gdkFuncDependencies: [gdk],
    afterSuccessDialog: handleBack
  })
  return (
    <StateProvider context={RequestContext} defaultValue={defaultContext}>
      <Box padding={5}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PlayerSearchForm context={RequestContext} loading={loading}/>
          </Grid>
          <Grid item>
            <ManualWithdrawCreateForm
              handleSubmit={handleSubmit}
              handleCancel={handleDebouncedBack}
              loading={loading}
            />
            <ManualWithdrawFileCreateForm
              loading={loading}
              setLoading={setLoading}
              handleBack={handleDebouncedBack}
            />
          </Grid>
        </Grid>
      </Box>
    </StateProvider>
  )
}

export default React.memo(ManualWithdrawCreatePage)
